/**
 * 套票組成設定（`category = 'package'` 的商品才會出現）。
 *
 * ## 為什麼這個畫面是必要的，不是加分項
 *
 * 套票商品不建班次：它只定義「含哪些商品」，顧客在機台上為每一段各挑一個時段，
 * 座位因此扣在真實班次上。後端對沒設組成的套票是 **fail-closed**——直接拒絕下單。
 * 所以沒有這個畫面，套票就永遠賣不出去。
 *
 * 上一版把組成掛在班次上，而寫入端從來沒做，結果是唯一在售的套票 0 列設定、完全
 * 不設防。這次把維護入口跟資料模型一起交付，就是為了不重演那件事。
 *
 * ## 整組取代
 *
 * 存檔送的是整份清單（PUT），不是逐列增刪。組成通常兩三段，整組送出比追蹤「哪一列
 * 被改過」單純。已售出的訂單不受影響——那些訂單的分段與金額在下單當下就寫死在
 * `order_items` 了，不會回頭讀這張表。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from './Button';
import {
  listProductComponents,
  replaceProductComponents,
  type ProductComponent,
} from '../lib/api/product-components';
import { listProductsLite, type ProductLite } from '../lib/api/products';

interface Row {
  component_product_id: string;
}

function pickName(i18n: Record<string, string>, fallback: string): string {
  return i18n['zh-TW'] ?? i18n['en'] ?? fallback;
}

export function PackageComponentsEditor({ productId }: { productId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const componentsQuery = useQuery({
    queryKey: ['product-components', productId],
    queryFn: () => listProductComponents(productId),
  });

  /*
   * 候選商品要排除套票：組成不可以是另一個套票（後端也擋），而巢狀套票會讓
   * 「展開成分段」變成無界遞迴。這裡先濾掉，使用者就不會選了才被退回。
   */
  const productsQuery = useQuery({ queryKey: ['products-lite'], queryFn: listProductsLite });
  const candidates: ProductLite[] = (productsQuery.data?.items ?? []).filter(
    (p) => p.id !== productId,
  );

  useEffect(() => {
    const loaded: ProductComponent[] | undefined = componentsQuery.data;
    if (loaded === undefined) return;
    setRows(loaded.map((c) => ({ component_product_id: c.component_product_id })));
  }, [componentsQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      replaceProductComponents(productId, {
        // sort_order 就是畫面上的順序；機台照這個順序一段一段問時間
        items: rows.map((r, i) => ({
          component_product_id: r.component_product_id,
          sort_order: i,
        })),
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['product-components', productId] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof Error ? e.message : '儲存失敗');
    },
  });

  const filled = rows.filter((r) => r.component_product_id !== '');
  const ids = filled.map((r) => r.component_product_id);
  const hasDupe = new Set(ids).size !== ids.length;
  const canSave = filled.length >= 2 && !hasDupe && filled.length === rows.length;

  function setRow(index: number, value: string): void {
    setSaved(false);
    setRows((prev) => prev.map((r, i) => (i === index ? { component_product_id: value } : r)));
  }

  function move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    setSaved(false);
    setRows((prev) => {
      const next = [...prev];
      const a = next[index] as Row;
      next[index] = next[target] as Row;
      next[target] = a;
      return next;
    });
  }

  return (
    <section style={{ marginTop: 32, borderTop: '1px solid #e5e7eb', paddingTop: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>套票組成</h2>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
        套票不建班次。這裡設定它含哪些體驗，顧客在購票機上為每一段各挑一個時段，座位扣在
        各段真實班次上。<strong>沒有設定組成的套票不可售</strong>——下單會被擋下。
      </p>

      {componentsQuery.isLoading && <p style={{ fontSize: 13 }}>載入中…</p>}

      <div style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#6b7280', width: 48 }}>第 {i + 1} 段</span>
            <select
              value={row.component_product_id}
              onChange={(e) => setRow(i, e.target.value)}
              style={{ flex: 1, padding: 6 }}
            >
              <option value="">— 選擇商品 —</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {pickName(p.name_i18n, p.sku)}（{p.sku}）
                </option>
              ))}
            </select>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0}>
              ↑
            </button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1}>
              ↓
            </button>
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setRows((prev) => prev.filter((_, idx) => idx !== i));
              }}
            >
              移除
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => {
            setSaved(false);
            setRows((prev) => [...prev, { component_product_id: '' }]);
          }}
        >
          ＋ 新增一段
        </button>
        <Button
          onClick={() => saveMut.mutate()}
          disabled={!canSave || saveMut.isPending}
          variant="primary"
        >
          {saveMut.isPending ? '儲存中…' : '儲存組成'}
        </Button>
        {saved && <span style={{ fontSize: 13, color: '#059669' }}>已儲存</span>}
      </div>

      {rows.length > 0 && filled.length !== rows.length && (
        <p style={{ fontSize: 13, color: '#b45309', marginTop: 8 }}>有一段還沒選商品。</p>
      )}
      {hasDupe && (
        <p style={{ fontSize: 13, color: '#b45309', marginTop: 8 }}>同一個商品重複出現。</p>
      )}
      {filled.length === rows.length && rows.length < 2 && (
        <p style={{ fontSize: 13, color: '#b45309', marginTop: 8 }}>
          至少要兩段——只含一段的不是套票。
        </p>
      )}
      {error !== null && <p style={{ fontSize: 13, color: '#b91c1c', marginTop: 8 }}>{error}</p>}
    </section>
  );
}
